import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { SiteHeader } from "@/components/Logo";
import DraftLobby from "@/components/DraftLobby";
import AuctionRoom from "@/components/AuctionRoom";
import DraftResults from "@/components/DraftResults";
import ChatDrawer from "@/components/ChatDrawer";
import { fetchDraft, wsUrl } from "@/lib/api";
import { getSession, saveSession, clearSession } from "@/lib/storage";
import { Button } from "@/components/ui/button";
import { Wifi, WifiOff, Volume2, VolumeX, HelpCircle, Bell, BellOff } from "lucide-react";
import {
  initSound,
  isSoundEnabled,
  setSoundEnabled,
  playBid,
  playHammer,
  playChime,
} from "@/lib/sound";

export default function DraftRoom() {
  const { code } = useParams();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const rejoinPid = searchParams.get("as");
  // Session is stable across renders to prevent the WS effect from
  // tearing down/reconnecting on every render. It can be swapped once
  // when a ?as=<pid> rejoin link is resolved.
  const [session, setSession] = useState(() => getSession(code));
  const [resolvingRejoin, setResolvingRejoin] = useState(!!rejoinPid);
  const [state, setState] = useState(null);
  const [connected, setConnected] = useState(false);
  const [chat, setChat] = useState([]);
  const [unreadChat, setUnreadChat] = useState(0);
  const [lastEvent, setLastEvent] = useState(null);
  const [soundOn, setSoundOnState] = useState(true);
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);
  const shouldReconnect = useRef(true);
  // Tracks last seen participant IDs to detect new joins (host-only notifications)
  const knownPidsRef = useRef(null);

  const fireBrowserNotification = (title, body) => {
    if (typeof window === "undefined" || !("Notification" in window)) return;
    if (Notification.permission !== "granted") return;
    if (document.visibilityState === "visible") return; // user is already looking
    try {
      const n = new Notification(title, {
        body,
        icon: "https://customer-assets.emergentagent.com/job_fairway-picks/artifacts/hbhwifnu_ChatGPT%20Image%20Jun%208%2C%202026%2C%2001_54_26%20PM.png",
        tag: `pbo-${code}-join`,
      });
      n.onclick = () => {
        window.focus();
        n.close();
      };
    } catch {
      // ignore
    }
  };

  const [notifPerm, setNotifPerm] = useState(
    typeof window !== "undefined" && "Notification" in window
      ? Notification.permission
      : "denied"
  );
  const requestNotifPerm = async () => {
    if (typeof window === "undefined" || !("Notification" in window)) {
      toast.error("Your browser doesn't support notifications.");
      return;
    }
    try {
      // Some older browsers use a callback signature; normalise to a promise
      // and always trust the global `Notification.permission` value afterwards.
      let p;
      try {
        p = await Notification.requestPermission();
      } catch {
        p = await new Promise((resolve) => {
          try {
            Notification.requestPermission(resolve);
          } catch {
            resolve(Notification.permission);
          }
        });
      }
      const finalPerm = p || Notification.permission;
      setNotifPerm(finalPerm);
      if (finalPerm === "granted") {
        toast.success("Notifications on — you'll get pinged when friends join.");
      } else if (finalPerm === "denied") {
        toast.error("Notifications blocked. Enable them in your browser settings.");
      }
    } catch {
      // ignore
    }
  };

  // Keep notification permission state in sync with the browser. Permissions
  // can change outside of this tab (e.g. site settings, another tab granting),
  // so re-read on mount, on visibility change, and via the Permissions API
  // status listener when available.
  useEffect(() => {
    if (typeof window === "undefined" || !("Notification" in window)) return;
    const sync = () => setNotifPerm(Notification.permission);
    sync();
    const onVis = () => {
      if (document.visibilityState === "visible") sync();
    };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", sync);

    let permStatus = null;
    if (navigator.permissions && navigator.permissions.query) {
      navigator.permissions
        .query({ name: "notifications" })
        .then((status) => {
          permStatus = status;
          setNotifPerm(status.state === "prompt" ? "default" : status.state);
          status.onchange = () => {
            setNotifPerm(status.state === "prompt" ? "default" : status.state);
          };
        })
        .catch(() => {});
    }

    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", sync);
      if (permStatus) permStatus.onchange = null;
    };
  }, []);

  useEffect(() => {
    initSound();
    setSoundOnState(isSoundEnabled());
  }, []);

  const toggleSound = () => {
    const next = !soundOn;
    setSoundEnabled(next);
    setSoundOnState(next);
  };

  // Resolve a ?as=<participant_id> rejoin link: fetch the draft, verify
  // the participant exists, swap session, strip the query param.
  useEffect(() => {
    if (!rejoinPid) return;
    let cancelled = false;
    fetchDraft(code)
      .then((d) => {
        if (cancelled) return;
        const target = d.participants.find((p) => p.id === rejoinPid);
        if (!target) {
          toast.error("That seat link isn't valid for this draft");
          nav(`/join/${code}`, { replace: true });
          return;
        }
        saveSession(code, target.id, target.username);
        setSession({ participantId: target.id, username: target.username });
        setState(d);
        setResolvingRejoin(false);
        nav(`/draft/${code}`, { replace: true });
        toast.success(`Welcome back, ${target.username}`);
      })
      .catch(() => {
        if (cancelled) return;
        toast.error("Draft not found");
        nav("/", { replace: true });
      });
    return () => {
      cancelled = true;
    };
  }, [rejoinPid, code, nav]);

  // No session AND no rejoin token => bounce to join
  useEffect(() => {
    if (!session && !rejoinPid && !resolvingRejoin) {
      nav(`/join/${code}`, { replace: true });
    }
  }, [session, code, nav, rejoinPid, resolvingRejoin]);

  // Initial state fetch (skipped while resolving a rejoin link – that flow sets state itself)
  useEffect(() => {
    if (!session || resolvingRejoin) return;
    fetchDraft(code)
      .then((d) => setState(d))
      .catch((e) => {
        toast.error("Draft not found");
        clearSession(code);
        nav("/", { replace: true });
      });
  }, [code, session, nav, resolvingRejoin]);

  const connect = useCallback(() => {
    if (!session) return;
    // Avoid duplicate sockets (StrictMode double-mount, fast re-renders)
    if (wsRef.current &&
        (wsRef.current.readyState === WebSocket.OPEN ||
         wsRef.current.readyState === WebSocket.CONNECTING)) {
      return;
    }
    const ws = new WebSocket(wsUrl(code, session.participantId));
    wsRef.current = ws;
    ws.onopen = () => setConnected(true);
    ws.onclose = () => {
      setConnected(false);
      if (!shouldReconnect.current) return;
      clearTimeout(reconnectTimer.current);
      reconnectTimer.current = setTimeout(connect, 1500);
    };
    ws.onerror = () => {
      setConnected(false);
    };
    ws.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);
        if (msg.type === "state") {
          // Detect a brand-new participant joining (host-only notification)
          const prevPids = knownPidsRef.current;
          const newPids = (msg.participants || []).map((p) => p.id);
          if (prevPids && session) {
            const me = msg.participants.find((p) => p.id === session.participantId);
            if (me?.is_host) {
              const added = msg.participants.filter((p) => !prevPids.has(p.id) && p.id !== session.participantId);
              for (const p of added) {
                const text = `${p.username} joined your draft`;
                toast.success(text);
                playChime();
                fireBrowserNotification("🏌️ Palm Breezy Open", text);
              }
            }
          }
          knownPidsRef.current = new Set(newPids);

          setState({
            draft: msg.draft,
            participants: msg.participants,
            players: msg.players,
            server_time: msg.server_time,
          });
          if (msg.event) {
            setLastEvent({ event: msg.event, ts: Date.now() });
            if (msg.event === "bid_placed") playBid();
            if (msg.event === "lot_started") playChime();
          }
        } else if (msg.type === "chat") {
          setChat((c) => [...c.slice(-99), { from: msg.from, text: msg.text, ts: msg.ts }]);
          setUnreadChat((n) => n + 1);
        } else if (msg.type === "lot_sold") {
          playHammer();
          toast.success(msg.log || `Sold for $${msg.price}`);
        } else if (msg.type === "lot_passed") {
          toast.message(msg.log || "Lot passed");
        } else if (msg.type === "draft_complete") {
          playHammer();
          toast.success("Draft complete!");
        } else if (msg.type === "error") {
          toast.error(msg.message || "Error");
        }
      } catch (err) {
        // ignore
      }
    };
  }, [code, session]);

  useEffect(() => {
    shouldReconnect.current = true;
    connect();
    return () => {
      shouldReconnect.current = false;
      clearTimeout(reconnectTimer.current);
      try {
        wsRef.current?.close();
      } catch {
        // ignore close errors
      }
    };
  }, [connect]);

  const send = useCallback((obj) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(obj));
    } else {
      toast.error("Not connected. Reconnecting…");
    }
  }, []);

  // Anti-quit guard: warn the user if they try to close the tab / reload
  // mid-draft. Only active when the draft is in progress AND this seat is a
  // drafter (spectators can safely leave at any time). The browser-controlled
  // generic "Leave site?" prompt is shown; modern browsers ignore the custom
  // message, so we don't bother setting one.
  const meRef = useRef(null);
  const draftStatusRef = useRef(null);
  useEffect(() => {
    meRef.current = state?.participants?.find((p) => p.id === session?.participantId) || null;
    draftStatusRef.current = state?.draft?.status || null;
  }, [state, session]);
  useEffect(() => {
    const onBeforeUnload = (e) => {
      const m = meRef.current;
      const status = draftStatusRef.current;
      if (status === "active" && m && !m.is_spectator) {
        e.preventDefault();
        // Required by Chrome to actually show the prompt
        e.returnValue = "";
        return "";
      }
      return undefined;
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, []);

  const leaveDraft = () => {
    // Confirm if we're abandoning an active draft as a drafter
    const status = state?.draft?.status;
    const m = state?.participants?.find((p) => p.id === session?.participantId);
    if (status === "active" && m && !m.is_spectator) {
      const ok = window.confirm(
        "The draft is still in progress.\n\nIf you leave now your seat stays in the draft but you'll need to rejoin to bid on your turn. Continue?"
      );
      if (!ok) return;
    }
    clearSession(code);
    nav("/");
  };

  if (!session || !state) {
    return (
      <div className="min-h-screen bg-pbo-cream">
        <SiteHeader />
        <div className="p-10 text-center text-pbo-muted">Loading draft room…</div>
      </div>
    );
  }

  const me = state.participants.find((p) => p.id === session.participantId);
  const draft = state.draft;

  return (
    <div className="min-h-screen bg-pbo-cream flex flex-col">
      <SiteHeader
        rightSlot={
          <div className="flex items-center gap-3">
            <div
              className="flex items-center gap-1.5 text-xs font-semibold"
              data-testid="ws-status"
              title={connected ? "Live" : "Reconnecting"}
            >
              {connected ? (
                <>
                  <Wifi className="w-4 h-4 text-pbo-green" />
                  <span className="text-pbo-green hidden sm:inline">Live</span>
                </>
              ) : (
                <>
                  <WifiOff className="w-4 h-4 text-pbo-muted" />
                  <span className="text-pbo-muted hidden sm:inline">Offline</span>
                </>
              )}
            </div>
            <div className="text-xs text-pbo-muted">
              <span className="font-mono font-semibold text-pbo-green tracking-[0.2em]">
                {draft.code}
              </span>
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={toggleSound}
              className="text-pbo-muted hover:text-pbo-green h-8 w-8 p-0"
              title={soundOn ? "Mute sounds" : "Unmute sounds"}
              data-testid="sound-toggle-btn"
            >
              {soundOn ? (
                <Volume2 className="w-4 h-4" />
              ) : (
                <VolumeX className="w-4 h-4" />
              )}
            </Button>
            {me?.is_host && notifPerm !== "granted" && (
              <Button
                size="sm"
                variant="ghost"
                onClick={requestNotifPerm}
                className={`h-8 w-8 p-0 ${notifPerm === "denied" ? "text-pbo-muted/40" : "text-pbo-gold animate-pulse"}`}
                title={
                  notifPerm === "denied"
                    ? "Notifications blocked — enable in browser settings"
                    : "Get notified when friends join"
                }
                data-testid="enable-notifications-btn"
              >
                {notifPerm === "denied" ? (
                  <BellOff className="w-4 h-4" />
                ) : (
                  <Bell className="w-4 h-4" />
                )}
              </Button>
            )}
            <a
              href="/help"
              target="_blank"
              rel="noopener noreferrer"
              className="text-pbo-muted hover:text-pbo-green h-8 w-8 grid place-items-center rounded"
              title="Help & rejoin instructions"
              data-testid="header-help-link"
            >
              <HelpCircle className="w-4 h-4" />
            </a>
            <Button
              size="sm"
              variant="ghost"
              onClick={leaveDraft}
              className="text-pbo-muted hover:text-pbo-green text-xs"
              data-testid="leave-draft-btn"
            >
              Leave
            </Button>
          </div>
        }
      />

      <main className="flex-1 flex flex-col">
        {draft.status === "lobby" && (
          <DraftLobby state={state} me={me} send={send} />
        )}
        {draft.status === "active" && (
          <AuctionRoom
            state={state}
            me={me}
            send={send}
            lastEvent={lastEvent}
          />
        )}
        {draft.status === "complete" && (
          <DraftResults state={state} me={me} />
        )}
      </main>

      <ChatDrawer
        chat={chat}
        unread={unreadChat}
        onMarkRead={() => setUnreadChat(0)}
        onSend={(text) => send({ type: "chat", text })}
        me={me}
      />
    </div>
  );
}
